import React, { useState, useRef, useCallback } from 'react';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    XCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy,
    Info
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import ticketService from '../../../api/crm/support/ticketService';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';

/**
 * BulkTicketUpload - Modal for bulk uploading tickets from CSV
 * Styled similar to BulkTargetUpload.jsx for a premium look
 */
const BulkTicketUpload = ({ isOpen, onComplete, onCancel }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedTickets, setParsedTickets] = useState([]);
    const [previewData, setPreviewData] = useState(null);   // validated rows from backend
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const fileInputRef = useRef(null);

    // ──────────────────────────────────────────────
    // Reset and Close
    // ──────────────────────────────────────────────
    const resetState = useCallback(() => {
        setStep('upload');
        setFile(null);
        setParsedTickets([]);
        setPreviewData(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onCancel();
    }, [resetState, onCancel]);

    // ──────────────────────────────────────────────
    // File Selection & Parsing
    // ──────────────────────────────────────────────
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        Papa.parse(selectedFile, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                if (!results.data || results.data.length === 0) {
                    toast.error('No valid ticket data found in CSV');
                    setFile(null);
                    return;
                }

                // Normalizing keys and values
                const normalized = results.data.map(row => {
                    const clean = {};
                    Object.keys(row).forEach(k => {
                        const val = row[k];
                        clean[k.trim()] = String(val == null ? '' : val).trim();
                    });
                    return clean;
                });

                setParsedTickets(normalized);
                setStep('preview');
                await validateTickets(normalized);
            },
            error: (err) => {
                toast.error(`CSV Parsing Error: ${err.message}`);
                setFile(null);
            }
        });
    };

    // ──────────────────────────────────────────────
    // Backend Validation
    // ──────────────────────────────────────────────
    const validateTickets = async (tickets) => {
        setIsValidating(true);
        try {
            const response = await ticketService.bulkPreview(tickets);
            if (response.success) {
                setPreviewData(response.data);
            } else {
                toast.error(response.error || 'Server validation failed');
            }
        } catch (err) {
            toast.error('Validation failed: ' + err.message);
        } finally {
            setIsValidating(false);
        }
    };

    // ──────────────────────────────────────────────
    // Final Upload Logic
    // ──────────────────────────────────────────────
    const handleUpload = async () => {
        const validTickets = previewData?.filter(r => r.isValid).map(r => r.resolvedData);
        if (!validTickets?.length) return;

        setIsUploading(true);
        setStep('uploading');
        try {
            const response = await ticketService.bulkUpload(validTickets);
            setUploadResult(response);
            setStep('result');

            if (response.success) {
                const count = response.data?.success || 0;
                toast.success(`Successfully uploaded ${count} tickets`);
                if (response.data?.failed > 0) {
                    toast.error(`${response.data.failed} rows failed to save.`);
                }
            } else {
                toast.error(response.error || 'Upload failed');
            }
        } catch (err) {
            setUploadResult({ success: false, error: err.message });
            setStep('result');
            toast.error(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    // ──────────────────────────────────────────────
    // Template Support
    // ──────────────────────────────────────────────
    const handleDownloadTemplate = () => {
        const headers = ['title', 'description', 'customer', 'priority', 'source', 'employeeId', 'orderNumber', 'category'];
        const samples = [
            ['Slow Internet Speed', 'Customer complaining about 10Mbps speed on 100Mbps plan.', 'customer@example.com', 'high', 'call', 'EMP001', 'ORD12345', 'Technical'],
            ['Billing Error', 'Overcharged on last month basic subscription.', 'Big Corp Inc.', 'medium', 'email', 'EMP002', '', 'Billing']
        ];

        const csvContent = [headers, ...samples]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ticket_bulk_upload_template.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Template downloaded');
    };

    // counts
    const validCount = previewData ? previewData.filter(r => r.isValid).length : 0;
    const errorCount = previewData ? previewData.filter(r => !r.isValid).length : 0;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <Upload className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Ticket Upload</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload a CSV to batch create support tickets'}
                                {step === 'preview' && (isValidating ? 'Validating CSV data...' : `${validCount} of ${parsedTickets.length} tickets valid`)}
                                {step === 'uploading' && 'Creating tickets...'}
                                {step === 'result' && 'Process finished'}
                            </p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="h-5 w-5 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* STEP 1: UPLOAD */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-indigo-500 hover:bg-indigo-50/20 transition-all cursor-pointer"
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const drFile = e.dataTransfer.files[0];
                                    if (drFile) handleFileChange({ target: { files: [drFile] } });
                                }}
                            >
                                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700">Drop your CSV file here</p>
                                <p className="text-sm text-gray-500 mt-1">or click to browse from files (Max 5MB)</p>
                                <div className="mt-5">
                                    <Button variant="outline" icon={<Upload className="h-4 w-4" />}>Select CSV</Button>
                                </div>
                            </div>

                            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 flex items-start space-x-4">
                                <FileText className="h-6 w-6 text-indigo-500 mt-1 flex-shrink-0" />
                                <div>
                                    <h4 className="text-sm font-semibold text-indigo-900 mb-1">Use the Standard Template</h4>
                                    <p className="text-xs text-indigo-700 mb-3">Ensure your CSV column headers match exactly to the system fields for better auto-mapping.</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownloadTemplate}
                                        icon={<Download className="h-3 w-3" />}
                                        className="bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                    >Download Template</Button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <h5 className="text-xs font-bold text-gray-500 uppercase mb-2">Required Fields</h5>
                                    <ul className="text-xs text-gray-700 space-y-1">
                                        <li><strong>title:</strong> Ticket summary</li>
                                        <li><strong>description:</strong> Detailed problem</li>
                                        <li><strong>customer:</strong> Email or Company Name</li>
                                    </ul>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <h5 className="text-xs font-bold text-gray-500 uppercase mb-2">Optional Fields</h5>
                                    <ul className="text-xs text-gray-700 space-y-1">
                                        <li><strong>priority:</strong> low, medium, high</li>
                                        <li><strong>source:</strong> call, email, website...</li>
                                        <li><strong>employeeId:</strong> ID of assignee</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: PREVIEW */}
                    {step === 'preview' && (
                        <div className="space-y-6">
                            {isValidating ? (
                                <div className="flex flex-col items-center justify-center p-12 space-y-4">
                                    <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
                                    <p className="text-gray-600 font-medium">Analyzing rows and matching customers...</p>
                                </div>
                            ) : previewData && (
                                <>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-center">
                                            <p className="text-2xl font-bold text-gray-700">{parsedTickets.length}</p>
                                            <p className="text-[10px] uppercase font-bold text-gray-400">Total Rows</p>
                                        </div>
                                        <div className={`rounded-lg p-3 border text-center ${errorCount > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                                            <p className={`text-2xl font-bold ${errorCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{errorCount}</p>
                                            <p className={`text-[10px] uppercase font-bold ${errorCount > 0 ? 'text-red-400' : 'text-gray-400'}`}>Errors Found</p>
                                        </div>
                                        <div className="bg-green-50 border-green-100 rounded-lg p-3 border text-center">
                                            <p className="text-2xl font-bold text-green-700">{validCount}</p>
                                            <p className="text-[10px] uppercase font-bold text-green-400">Valid & Ready</p>
                                        </div>
                                    </div>

                                    {errorCount > 0 && (
                                        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-semibold text-red-800 flex items-center">
                                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                                    Validation Failed for {errorCount} rows
                                                </h4>
                                                <button
                                                    onClick={() => {
                                                        const txt = previewData.filter(r => !r.isValid)
                                                            .map((r, i) => `Row ${i + 1}: ${r.errors.join('; ')}`).join('\n');
                                                        navigator.clipboard.writeText(txt);
                                                        toast.success('Errors copied to clipboard');
                                                    }}
                                                    className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-1 rounded-md flex items-center gap-1 hover:bg-red-200"
                                                >
                                                    <Copy className="h-3 w-3" /> Copy Logs
                                                </button>
                                            </div>
                                            <div className="max-h-32 overflow-y-auto space-y-1 pr-2">
                                                {previewData.filter(r => !r.isValid).map((row, i) => (
                                                    <div key={i} className="text-[11px] text-red-700 font-medium">
                                                        <span className="font-bold">#{i + 1}:</span> {row.errors.join(', ')}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="min-w-full text-xs">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Status</th>
                                                    <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Title</th>
                                                    <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Customer Match</th>
                                                    <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Assignee</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {previewData.slice(0, 10).map((row, i) => (
                                                    <tr key={i} className={row.isValid ? 'hover:bg-gray-50' : 'bg-red-50/30'}>
                                                        <td className="px-4 py-3">
                                                            {row.isValid ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                                                        </td>
                                                        <td className="px-4 py-3 font-medium text-gray-900 truncate max-w-[150px]" title={row.title}>{row.title}</td>
                                                        <td className="px-4 py-3 text-gray-600">
                                                            {row.resolvedCustomerName ? (
                                                                <div className="flex flex-col">
                                                                    <span className="font-bold">{row.resolvedCustomerName}</span>
                                                                    <span className="text-[10px] text-gray-400">ID: {row.resolvedCustomerId}</span>
                                                                </div>
                                                            ) : <span className="text-red-500 italic">Not found</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-600">{row.resolvedAssigneeName || <span className="text-gray-400 italic">Unassigned</span>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {previewData.length > 10 && (
                                            <div className="bg-gray-50 px-4 py-2 text-[10px] text-gray-400 text-right italic font-medium">
                                                (+ {previewData.length - 10} more rows hidden)
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* STEP 3: UPLOADING */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 className="h-14 w-14 animate-spin text-indigo-600 mb-6" />
                            <h3 className="text-xl font-bold text-gray-900">Uploading to CRM</h3>
                            <p className="text-gray-500 mt-2">Processing batch creation and system emails...</p>
                        </div>
                    )}

                    {/* STEP 4: RESULT */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`rounded-2xl p-8 text-center ${uploadResult.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                                {uploadResult.success ? (
                                    <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
                                ) : (
                                    <XCircle className="h-20 w-20 text-red-500 mx-auto mb-4" />
                                )}
                                <h3 className={`text-2xl font-bold ${uploadResult.success ? 'text-green-900' : 'text-red-900'}`}>
                                    {uploadResult.success ? 'Bulk Upload Complete!' : 'Upload Encountered Errors'}
                                </h3>
                                <p className={`mt-2 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                    {uploadResult.error || (uploadResult.success ? 'All tickets have been successfully created.' : 'Check the details below.')}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
                                    <p className="text-3xl font-bold text-indigo-600">{uploadResult.data?.success || 0}</p>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mt-1">Successfully Created</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm">
                                    <p className="text-3xl font-bold text-red-400">{uploadResult.data?.failed || 0}</p>
                                    <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mt-1">Failed to Save</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <div>
                        {step === 'preview' && (
                            <Button variant="outline" onClick={() => setStep('upload')}>Back to Upload</Button>
                        )}
                        {step !== 'uploading' && step !== 'result' && (
                            <Button variant="outline" onClick={handleClose}>Cancel</Button>
                        )}
                    </div>
                    <div>
                        {step === 'preview' && !isValidating && (
                            <Button
                                variant="primary"
                                className="bg-indigo-600 hover:bg-indigo-700 px-8"
                                disabled={validCount === 0 || isUploading}
                                onClick={handleUpload}
                                icon={<Upload className="h-4 w-4" />}
                            >
                                {isUploading ? 'Uploading...' : `Upload ${validCount} Tickets`}
                            </Button>
                        )}
                        {step === 'result' && (
                            <Button
                                variant="primary"
                                className="bg-indigo-600 hover:bg-indigo-700 px-10"
                                onClick={() => { if (uploadResult.success) onComplete(); handleClose(); }}
                            >
                                Done
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkTicketUpload;
